import { PostSource, PostStatus } from './constants';
import type { BusinessLiteDto, BusinessModel } from '../business/types';

type ClickOutDto = {
    url: string;
    url_display: string;
    target: 'button';
    text: string;
};

export enum ReactionState {
    LIKE = 'like',
    DISLIKE = 'dislike',
    NONE = 'none',
}

export interface PostDto {
    id: string;
    text: string;
    likes: number;
    views: number;
    reaction_state: ReactionState;
    saved: boolean;
    tags: string[];
    status: PostStatus;
    post_url: string;
    data_source: PostSource;
    business: BusinessLiteDto;
    clickouts: ClickOutDto[];
    content: Array<
        | {
              id: string;
              type: 'image';
              media: Array<{
                  url: string;
                  width: number;
                  height: number;
              }>;
          }
        | {
              id: string;
              type: 'video';
              media: Inspire.VideoDto[];
          }
    >;
    created_at: string;
    dj_request_id?: string;
}

type ClickOutModel = {
    url: string;
    urlDisplay: string;
    target: 'button';
    text: string;
};

export interface PostModel
    extends Omit<
        PostDto,
        | 'business'
        | 'categories'
        | 'content'
        | 'created_at'
        | 'clickouts'
        | 'data_source'
        | 'post_url'
        | 'reaction_state'
    > {
    createdAt: string;
    url: string;
    source: PostSource;
    shareUrl: string;
    djRequestId?: string;
    clickouts: ClickOutModel[];
    business: BusinessModel;
    reactionState: ReactionState;
    content: Array<
        | {
              id: string;
              type: 'image';
              media: Inspire.Image[];
          }
        | {
              id: string;
              type: 'video';
              media: Inspire.Video[];
          }
    >;
}

export interface PostDataModel extends Omit<PostModel, 'business' | 'categories'> {
    business: BusinessModel['id'];
}

export interface PostPersistenceDto {
    business_id: string;
    text: string;
    media?: Array<{
        id: string;
        url: string;
        type: 'image' | 'video';
    }>;
    status?: PostStatus;
    data_source: PostSource;
    clickouts: Array<{
        url: string;
        url_display: string;
        text: string;
        target: 'button';
    }>;
}

export interface PostPersistenceModel {
    id?: string;
    text: string;
    businessId: string;
    status?: PostStatus;
    source?: PostSource;
    media?: Array<{
        id: string;
        url: string;
        type: 'image' | 'video';
    }>;
    clickouts: Array<{
        url: string;
        urlDisplay: string;
        text: string;
        target: 'button';
    }>;
}

export enum ReportReasons {
    OTHER = '0',
    VIOLENCE = '1',
    TRAGIC_CONTENT = '2',
    OFFENSIVE_REMARKS = '3',
    ADULT_CONTENT = '4',
    FRAUD = '5',
    SPAM_AND_ILLEGAL_CONTENT = '6',
    SELF_MEDICATION = '7',
    VIOLATION_OF_LAW = '8',
    CONTENT_INCONSISTENCY = '9',
}

export interface PostStatItem {
    value: number;
    display: string;
}

export interface PostStatModel {
    coverage: {
        total: PostStatItem;
        subscribers: PostStatItem;
        notsubscribers: PostStatItem;
    };
    involved: {
        total: PostStatItem;
        subscribers: PostStatItem;
        notsubscribers: PostStatItem;
    };
    shows: {
        total: PostStatItem;
        subscribers: PostStatItem;
        notsubscribers: PostStatItem;
    };
    subscribers: {
        total: PostStatItem;
        subscribed: PostStatItem;
        unsubscribed: PostStatItem;
    };
    interactions: {
        total: PostStatItem;
        clicks: PostStatItem;
        likes: PostStatItem;
        shares: PostStatItem;
        saves: PostStatItem;
    };
}

export interface PostStatDto extends PostStatModel {}
